import type { Dispatch, SetStateAction } from "react";
import {
  MbscCalendarEvent,
  MbscEventUpdateEvent,
  MbscPageLoadedEvent,
  MbscPageLoadingEvent
} from "@mobiscroll/react";
import { AxiosResponse } from "axios";
import { ProviderContext } from "notistack";
import { DateTime } from "luxon";
import { TFunction } from "i18next";
import {
  BookingScheduleInterface,
  RestrictionZone
} from "../../../features/Booking-Form/typings/booking.types";
import { TeamMember } from "../../../features/Booking-Form/typings/team-member";
import { PricingModel } from "../../../features/Login/typings/login.types";
import { UpdateBookingTimes } from "../../../features/Schedule/schedule.types";
import i18n from "../../../i18n";
import {
  getNoPlaceZoneBookingSchedule,
  getPlaceSchedule,
  getPlaceZoneSchedule,
  updateBookingTimes,
  updateConferenceZoneBookingTimes,
  updateVehicleBookingTimes
} from "../../../utils/axios-requests";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";

export const getWorkplacesOrZones = (
  startDate: string,
  endDate: string,
  mode: string,
  users: TeamMember[] | undefined,
  userInfo: { sub: string; pricingModels: PricingModel[] },
  selectedRestriction: RestrictionZone | undefined,
  selectedTypes: string[]
): Promise<AxiosResponse<BookingScheduleInterface[]>> => {
  /** currently don't use placeZone call */
  const useRequest = false;

  if (mode === "place") {
    return getPlaceSchedule({
      userIds: users ? users.map((user: { userId: string }) => user.userId) : [userInfo.sub],
      startDate,
      bookingType: selectedTypes,
      endDate
    });
    /* istanbul ignore next */
  } else if (
    useRequest &&
    mode === "zone" &&
    !userInfo.pricingModels.includes(PricingModel.ENTERPRISE)
  ) {
    return getPlaceZoneSchedule({
      userIds: [userInfo.sub],
      startDate,
      endDate
    });
    /* istanbul ignore next */
  } else if (useRequest && mode === "zone") {
    return getPlaceZoneSchedule({
      userIds: [userInfo.sub],
      startDate,
      endDate,
      zoneBookingObject: selectedRestriction?.object.zoneBookingObject,
      client: selectedRestriction?.company.id || undefined
    });
  } else {
    return getNoPlaceZoneBookingSchedule({
      userIds: users
        ? users.map((colleague: { userId: string }) => colleague.userId)
        : [userInfo.sub],
      startDate,
      endDate
    });
  }
};

export const checkIndex = (
  calendarData: MbscCalendarEvent[],
  dto: UpdateBookingTimes,
  setCalendarData?: Dispatch<SetStateAction<MbscCalendarEvent[]>>
) => {
  const resizedIndex = calendarData.findIndex(c => c.id === dto?.event?.event.id);
  if (resizedIndex >= 0) {
    const resetCalendarData = calendarData.slice();
    resetCalendarData[resizedIndex] = dto?.event.oldEvent;
    setCalendarData?.(resetCalendarData);
  }
};

export const updateTimes = async (
  dto: UpdateBookingTimes,
  schedule: BookingScheduleInterface | undefined,
  calendarData: MbscCalendarEvent[],
  {
    enqueueSnackbar,
    setCalendarData,
    refetchAllSchedule,
    cancelChanges
  }: {
    enqueueSnackbar: ProviderContext["enqueueSnackbar"];
    setCalendarData?: Dispatch<SetStateAction<MbscCalendarEvent[]>>;
    refetchAllSchedule?: () => void;
    cancelChanges?: () => void;
  }
) => {
  if (!schedule) return;
  if (schedule.bookingType === BookingType.CONFERENCEZONE) {
    try {
      await updateConferenceZoneBookingTimes({
        ...dto,
        bookingType: schedule.bookingType as BookingType
      });
      refetchAllSchedule?.();
      enqueueSnackbar(i18n.t("Successfully edited booking"), { variant: "success" });
      return;
    } catch (error: any) {
      cancelChanges?.();
      enqueueSnackbar(i18n.t(`${error.response.data.message}`), { variant: "error" });
      return;
    }
  }

  if (schedule.bookingType === BookingType.VEHICLE) {
    try {
      await updateVehicleBookingTimes({
        bookingId: dto.bookingId,
        newStartTime: dto.newStartTime,
        newEndTime: dto.newEndTime,
        bookingType: BookingType.VEHICLE,
        colleagueId: dto.colleagueId
      });
      refetchAllSchedule?.();
      enqueueSnackbar(i18n.t("Successfully edited booking"), { variant: "success" });
      return;
    } catch (error: any) {
      checkIndex(calendarData, dto, setCalendarData);
      cancelChanges?.();
      enqueueSnackbar(i18n.t(`${error.response.data.message}`), { variant: "error" });
      return;
    }
  }

  try {
    await updateBookingTimes({ ...dto, bookingType: schedule.bookingType as BookingType });
    refetchAllSchedule?.();
    enqueueSnackbar(i18n.t("Successfully edited booking"), { variant: "success" });
    return;
  } catch (error: any) {
    checkIndex(calendarData, dto, setCalendarData);
    cancelChanges?.();
    // Currently, there are no translations for errors in the backend
    // We show only a generic error message due to this reason
    // in the future, we show the backends error message instead!
    return enqueueSnackbar(i18n.t(`${error.response.data.message}`), { variant: "error" });
  }
};

export const checkInvalidCreateOnSchedule = (
  bookingStartDate: DateTime,
  bookingsOnSaturdaysAllowed: boolean | undefined,
  bookingsOnSundaysAllowed: boolean | undefined,
  enqueueSnackbar: ProviderContext["enqueueSnackbar"],
  t: TFunction
) => {
  // compare selected date with now time
  const todayDate = bookingStartDate.diffNow().as("day");

  if (todayDate < -1) return true;

  // consider saturday, sunday booking allowed settings
  if (
    (!bookingsOnSaturdaysAllowed && bookingStartDate.weekday === 6) ||
    (!bookingsOnSundaysAllowed && bookingStartDate.weekday === 7)
  ) {
    const weekdayInfo = bookingStartDate.weekday === 6 ? t("Saturday") : t("Sunday");
    enqueueSnackbar(t("_scheduleCreateOnCalendarWeekendCheck", { day: weekdayInfo }), {
      variant: "info"
    });
    return true;
  }

  return false;
};

export type CalendarDateRangeState = {
  firstDay: string | undefined;
  lastDay: string | undefined;
};

/** Used when the calendar is not mounted (e.g. table view) so schedule queries have a range. */
export function getInitialVisibleMonthCalendarDateRange(): CalendarDateRangeState {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return {
    firstDay: firstDay.toISOString(),
    lastDay: lastDay.toISOString()
  };
}

export function updateCalendarDate(
  currentLoadingEvent: MbscPageLoadingEvent | MbscPageLoadedEvent | undefined,
  setCalendarDate: Dispatch<SetStateAction<CalendarDateRangeState>>,
  refetchAllSchedule?: () => void
) {
  if (!currentLoadingEvent) return;
  const { firstDay, lastDay } = currentLoadingEvent;

  // ISO-8601 with `T` (not a space) so query-string encoding stays valid for APIs that use DateTime.fromISO (e.g. bookable-booking-objects).
  const next: CalendarDateRangeState = {
    firstDay: firstDay.toISOString(),
    lastDay: lastDay.toISOString()
  };

  setCalendarDate(prev =>
    prev.firstDay === next.firstDay && prev.lastDay === next.lastDay ? prev : next
  );

  refetchAllSchedule?.();
}

// prohibit the updateTimes request when the result is false
export function compareOldEventTime(
  event: MbscEventUpdateEvent,
  calendarData: MbscCalendarEvent[],
  schedules: BookingScheduleInterface[],
  setCalendarData: (value: React.SetStateAction<MbscCalendarEvent[]>) => void,
  enqueueSnackbar: ProviderContext["enqueueSnackbar"]
): boolean {
  // Reset the changes by reverting to oldEvent
  const resetUpdate = (message: string | null) => {
    const updatedCalendarData = calendarData.map(ev =>
      ev.id === event.event.id && event.oldEvent ? event.oldEvent : ev
    );
    setCalendarData(updatedCalendarData);

    if (message) enqueueSnackbar(message, { variant: "error" });
  };

  if (!event.oldEvent) return true;

  const schedulesByUser = calendarData.filter(cal =>
    schedules.some(schedule => schedule.id === cal.id)
  );

  // try to update what user doesn't book the schedule
  if (!schedulesByUser.some(schedule => schedule.id === event.event.id)) {
    resetUpdate(i18n.t("You can only change your own bookings"));
    return false;
  }

  const oldStart = new Date(event.oldEvent.start as string).getTime();
  const oldEnd = new Date(event.oldEvent.end as string).getTime();
  const newStart = new Date(event.event.start as string).getTime();
  const newEnd = new Date(event.event.end as string).getTime();
  const now = Date.now();

  // Allow if the start and end times are unchanged
  if (oldStart === newStart && oldEnd === newEnd) return false;

  // Allow only end time is changed
  if (oldStart === newStart && oldEnd !== newEnd) return true;

  // Check if the new start time is in the past
  if (oldStart < now && oldStart !== newStart) {
    resetUpdate(i18n.t("You are not allowed to change booking times in the past!"));
    return false;
  }

  return true;
}
